import { z } from 'zod';

/** Contratos de autenticação (M1). Fonte única entre front e back. */

export const userRoleSchema = z.enum(['admin', 'user']);
export type UserRole = z.infer<typeof userRoleSchema>;

/** Fluxo de aprovação do cadastro aberto. Só 'active' faz login. */
export const userStatusSchema = z.enum(['pending', 'active', 'rejected']);
export type UserStatus = z.infer<typeof userStatusSchema>;

/** Preferência de tema — 'system' segue o `prefers-color-scheme` do SO. */
export const themePrefSchema = z.enum(['light', 'dark', 'system']);
export type ThemePref = z.infer<typeof themePrefSchema>;

/** WhatsApp: normaliza para só dígitos (com DDD). Não é confirmado, só coletado. */
export const whatsappSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\D/g, ''))
  .refine((d) => d.length >= 10 && d.length <= 13, 'Informe um WhatsApp válido com DDD.');

export const publicUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  role: userRoleSchema,
  phone: z.string().nullable(),
  theme: themePrefSchema,
  supportIntroDismissedAt: z.string().nullable(),
});
export type PublicUser = z.infer<typeof publicUserSchema>;

export const loginBodySchema = z.object({
  email: z.string().trim().toLowerCase().email('E-mail inválido.'),
  password: z.string().min(1, 'Informe a senha.'),
});
export type LoginBody = z.infer<typeof loginBodySchema>;

export const registerBodySchema = z.object({
  /** Cadastro é aberto; convite é opcional (retrocompat, não é mais exigido). */
  inviteCode: z.string().trim().optional(),
  name: z.string().trim().min(2, 'Informe seu nome.').max(80),
  email: z.string().trim().toLowerCase().email('E-mail inválido.'),
  password: z.string().min(8, 'A senha precisa de pelo menos 8 caracteres.').max(200),
  phone: whatsappSchema,
});
export type RegisterBody = z.infer<typeof registerBodySchema>;

/** Resposta do cadastro aberto: a conta nasce pendente e aguarda aprovação (sem login). */
export const registerResponseSchema = z.object({ status: userStatusSchema });
export type RegisterResponse = z.infer<typeof registerResponseSchema>;

/** Resposta de login/refresh/register: access token + usuário. */
export const sessionResponseSchema = z.object({
  accessToken: z.string(),
  user: publicUserSchema,
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

export const meResponseSchema = z.object({ user: publicUserSchema });
export type MeResponse = z.infer<typeof meResponseSchema>;

// --- Convites (somente admin) ---

export const inviteSchema = z.object({
  id: z.string(),
  code: z.string(),
  /** Se preenchido, só este e-mail pode usar o convite. */
  email: z.string().email().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  usedAt: z.string().nullable(),
  usedByName: z.string().nullable(),
});
export type Invite = z.infer<typeof inviteSchema>;

export const createInviteBodySchema = z.object({
  email: z.string().trim().toLowerCase().email().optional(),
  expiresInDays: z.number().int().min(1).max(90).default(7),
});
export type CreateInviteBody = z.infer<typeof createInviteBodySchema>;

export const invitesResponseSchema = z.object({ invites: z.array(inviteSchema) });
export type InvitesResponse = z.infer<typeof invitesResponseSchema>;

// --- Segurança (M7): troca de senha e dispositivos/sessões ---

export const changePasswordBodySchema = z.object({
  currentPassword: z.string().min(1, 'Informe a senha atual.'),
  newPassword: z.string().min(8, 'A nova senha precisa de pelo menos 8 caracteres.').max(200),
});
export type ChangePasswordBody = z.infer<typeof changePasswordBodySchema>;

/** Preferências do usuário (M8): tema. Corpo do PATCH /api/auth/preferences. */
export const updatePreferencesBodySchema = z.object({
  theme: themePrefSchema,
});
export type UpdatePreferencesBody = z.infer<typeof updatePreferencesBodySchema>;

/** Sessão ativa = família de refresh token (um dispositivo). */
export const sessionInfoSchema = z.object({
  familyId: z.string(),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  isCurrent: z.boolean(),
});
export type SessionInfo = z.infer<typeof sessionInfoSchema>;

export const sessionsResponseSchema = z.object({ sessions: z.array(sessionInfoSchema) });
export type SessionsResponse = z.infer<typeof sessionsResponseSchema>;

// --- Administração de usuários (role=admin): aprovar / recusar / excluir ---

export const adminUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  phone: z.string().nullable(),
  role: userRoleSchema,
  status: userStatusSchema,
  createdAt: z.string(),
  /** true para a própria conta do admin logado (bloqueia ações sobre si mesmo). */
  isSelf: z.boolean(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const adminUsersResponseSchema = z.object({ users: z.array(adminUserSchema) });
export type AdminUsersResponse = z.infer<typeof adminUsersResponseSchema>;
